import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  RefreshCw,
  AlertCircle,
  X,
  Save,
  Building,
  Filter,
  Users,
  UserPlus,
  Loader2
} from 'lucide-react';
import { useAuth, useToken } from '@/store/hooks';

interface DepartmentRepresentativeBrief {
  user_id: string;
  member_id: string | null;
  name: string;
  phone: string | null;
  email?: string | null;
}

interface Department {
  id: string;
  name: string;
  code: string;
  description?: string;
  head_user_id?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  /** From /departments/heads/list merge */
  head_name?: string | null;
  head_phone?: string | null;
  head_email?: string | null;
  representatives?: DepartmentRepresentativeBrief[];
}

interface DeptMemberRow {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  member_id?: string;
  role: string;
  status: string;
}

interface FormData {
  name: string;
  description: string;
  status: 'active' | 'inactive';
}

interface FormErrors {
  name?: string;
  general?: string;
}

const DepartmentsPage = () => {
  const { user } = useAuth();
  const token = useToken();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [filteredDepartments, setFilteredDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    status: 'active'
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const [listModalDept, setListModalDept] = useState<Department | null>(null);
  const [listSearch, setListSearch] = useState('');
  const [listMembers, setListMembers] = useState<DeptMemberRow[]>([]);
  const [listLoading, setListLoading] = useState(false);

  // Check if user is universal admin
  const isUniversalAdmin = user?.role === 'universal_admin';

  useEffect(() => {
    if (isUniversalAdmin) {
      fetchDepartments();
    }
  }, [isUniversalAdmin]);

  useEffect(() => {
    filterDepartments();
  }, [departments, searchTerm, statusFilter]);

  const fetchDepartments = async () => {
    setIsLoading(true);
    try {
      const [deptRes, headsRes] = await Promise.all([
        fetch('/api/v1/departments', {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch('/api/v1/departments/heads/list', {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (!deptRes.ok) return;

      const deptData = await deptRes.json();
      const base: Department[] = deptData.data || [];

      let headMap = new Map<
        string,
        {
          head_name: string | null;
          head_phone: string | null;
          head_email: string | null;
          head_user_id: string | null;
          representatives: DepartmentRepresentativeBrief[];
        }
      >();
      if (headsRes.ok) {
        const headsData = await headsRes.json();
        const rows = headsData.data || [];
        headMap = new Map(
          rows.map((r: any) => [
            r.id,
            {
              head_name: r.head_name ?? null,
              head_phone: r.head_phone ?? null,
              head_email: r.head_email ?? null,
              head_user_id: r.head_user_id ?? null,
              representatives: Array.isArray(r.representatives) ? r.representatives : []
            }
          ])
        );
      }

      setDepartments(
        base.map((d) => {
          const h = headMap.get(d.id);
          return h
            ? {
                ...d,
                head_user_id: h.head_user_id ?? d.head_user_id,
                head_name: h.head_name,
                head_phone: h.head_phone,
                head_email: h.head_email,
                representatives: h.representatives
              }
            : d;
        })
      );
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDepartmentMembers = useCallback(async () => {
    if (!listModalDept || !token) return;
    setListLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('department_id', listModalDept.id);
      params.set('limit', '100');
      params.set('page', '1');
      if (listSearch.trim()) params.set('search', listSearch.trim());

      const response = await fetch(`/api/v1/users?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const users = data.data?.users || [];
        const filtered = users.filter((u: DeptMemberRow) => u.role !== 'universal_admin');
        setListMembers(filtered);
      } else {
        setListMembers([]);
      }
    } catch (e) {
      console.error('Error loading department members:', e);
      setListMembers([]);
    } finally {
      setListLoading(false);
    }
  }, [listModalDept, listSearch, token]);

  useEffect(() => {
    if (!listModalDept) return;
    const delay = listSearch.trim() ? 400 : 0;
    const t = setTimeout(() => {
      fetchDepartmentMembers();
    }, delay);
    return () => clearTimeout(t);
  }, [listModalDept, listSearch, fetchDepartmentMembers]);

  const openMembersModal = (department: Department) => {
    setListModalDept(department);
    setListSearch('');
    setListMembers([]);
  };

  const closeMembersModal = () => {
    setListModalDept(null);
    setListSearch('');
    setListMembers([]);
  };

  const filterDepartments = () => {
    let filtered = departments;

    if (searchTerm) {
      filtered = filtered.filter(department =>
        department.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        department.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        department.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(department => department.status === statusFilter);
    }

    setFilteredDepartments(filtered);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Department name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const url = modalMode === 'create' 
        ? '/api/v1/departments' 
        : `/api/v1/departments/${editingDepartment?.id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';

      if (!token) {
        setErrors({
          general: 'Authentication required. Please login again.'
        });
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        
        if (modalMode === 'create') {
          setDepartments(prev => [...prev, data.data]);
        } else {
          setDepartments(prev => prev.map(dept => 
            dept.id === editingDepartment?.id ? data.data : dept
          ));
        }
        
        closeModal();
      } else {
        let errorMessage = 'Failed to save department';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          console.error('Department save error:', errorData);
        } catch (e) {
          console.error('Failed to parse error response:', e);
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        setErrors({
          general: errorMessage
        });
      }
    } catch (error) {
      console.error('Error saving department:', error);
      setErrors({
        general: 'Network error. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (department: Department) => {
    setEditingDepartment(department);
    setModalMode('edit');
    setFormData({
      name: department.name,
      description: department.description || '',
      status: department.status
    });
    setShowModal(true);
  };

  const handleDelete = async (departmentId: string) => {
    if (!window.confirm('Are you sure you want to delete this department?')) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/departments/${departmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setDepartments(prev => prev.filter(dept => dept.id !== departmentId));
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to delete department');
      }
    } catch (error) {
      console.error('Error deleting department:', error);
      alert('Network error. Please try again.');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDepartment(null);
    setModalMode('create');
    setFormData({
      name: '',
      description: '',
      status: 'active'
    });
    setErrors({});
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };



  if (!isUniversalAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        {/* Header Section */}
        <div className="mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                <Building className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-1">Departments</h1>
                <p className="text-sm text-gray-600">Manage and organize your RWA departments</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchDepartments}
                className="inline-flex items-center px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={() => {
                  setModalMode('create');
                  setEditingDepartment(null);
                  setShowModal(true);
                }}
                className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Department
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filters Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 mb-6">
          <div className="p-5 lg:p-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="h-9 w-9 rounded-lg bg-blue-100 flex items-center justify-center">
                <Filter className="h-4 w-4 text-blue-600" />
              </div>
              <h2 className="text-base font-semibold text-gray-800">Search & Filter</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Search Departments</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Type to search departments..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 w-full h-10 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 focus:bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full h-10 px-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 focus:bg-white text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            {(searchTerm || statusFilter !== 'all') && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <X className="h-3 w-3 mr-1.5" />
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Departments Grid */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                All Departments
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Showing {filteredDepartments.length} {filteredDepartments.length === 1 ? 'department' : 'departments'}
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-24">
              <div className="text-center">
                <div className="animate-spin rounded-full h-14 w-14 border-4 border-blue-100 border-t-blue-600 mx-auto mb-4"></div>
                <p className="text-sm text-gray-500 font-medium">Loading departments...</p>
              </div>
            </div>
          ) : filteredDepartments.length === 0 ? (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 p-16 text-center">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center mx-auto mb-5">
                <Building className="h-10 w-10 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No departments found</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters to find what you\'re looking for.'
                  : 'Create your first department to get started managing your RWA organization.'}
              </p>
              {(!searchTerm && statusFilter === 'all') && (
                <button
                  onClick={() => {
                    setModalMode('create');
                    setEditingDepartment(null);
                    setShowModal(true);
                  }}
                  className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Department
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
              {filteredDepartments.map((department, index) => {
                const colors = [
                  'from-blue-500 to-cyan-500',
                  'from-purple-500 to-pink-500',
                  'from-green-500 to-emerald-500',
                  'from-orange-500 to-red-500',
                  'from-indigo-500 to-blue-500',
                  'from-pink-500 to-rose-500'
                ];
                const bgColors = [
                  'bg-blue-50',
                  'bg-purple-50',
                  'bg-green-50',
                  'bg-orange-50',
                  'bg-indigo-50',
                  'bg-pink-50'
                ];
                const colorIndex = index % colors.length;
                
                return (
                  <div
                    key={department.id}
                    className="group bg-white rounded-2xl shadow-md border-2 border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all duration-300 overflow-hidden"
                  >
                    <div className="p-5">
                      {/* Header with Icon */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className={`flex-shrink-0 h-14 w-14 rounded-xl bg-gradient-to-br ${colors[colorIndex]} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                            <Building className="h-7 w-7 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-bold text-gray-900 truncate mb-0.5">
                              {department.name}
                            </h3>
                            <p className="text-xs font-mono text-gray-400">
                              {department.code}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => openMembersModal(department)}
                            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                            title="Members in this department — search, edit, or add"
                          >
                            <Users className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(department)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                            title="Edit department"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(department.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Department representatives (from user_departments / heads list) */}
                      <div className="mb-3 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                          Representatives
                        </p>
                        {department.representatives && department.representatives.length > 0 ? (
                          <ul className="text-sm text-gray-800 space-y-1">
                            {department.representatives.map((rep) => (
                              <li key={rep.user_id}>
                                <span className="font-medium">{rep.name}</span>
                                {rep.phone && <span className="text-gray-500 ml-2">{rep.phone}</span>}
                              </li>
                            ))}
                          </ul>
                        ) : department.head_name ? (
                          <div className="text-sm text-gray-800">
                            <span className="font-medium">{department.head_name}</span>
                            {department.head_phone && (
                              <span className="text-gray-500 ml-2">{department.head_phone}</span>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Not assigned</p>
                        )}
                      </div>

                      {/* Description */}
                      {department.description && (
                        <div className="mb-4">
                          <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                            {department.description}
                          </p>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => openMembersModal(department)}
                        className="mt-1 w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-xl border border-indigo-100 transition-colors"
                      >
                        <Users className="h-4 w-4 shrink-0" />
                        List members
                      </button>

                      {/* Status Badge */}
                      <div className="pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {department.status === 'active' ? (
                              <>
                                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                                <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700 border border-green-200">
                                  Active
                                </span>
                              </>
                            ) : (
                              <>
                                <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                                <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                                  Inactive
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Members assigned to department — search, open full editor on Members page */}
      {listModalDept && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4">
          <div className="relative mt-8 mb-8 w-full max-w-3xl rounded-xl bg-white shadow-xl border border-gray-200 max-h-[90vh] flex flex-col">
            <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Members — {listModalDept.name}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Search by name or phone. Edit opens the same member form as on Residential Members. Add member
                  pre-selects this department.
                </p>
              </div>
              <button
                type="button"
                onClick={closeMembersModal}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 border-b border-gray-100">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={listSearch}
                    onChange={(e) => setListSearch(e.target.value)}
                    placeholder="Search by name, member ID, or phone..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    navigate(
                      `/admin/members?department_id=${encodeURIComponent(listModalDept.id)}&action=create`
                    );
                    closeMembersModal();
                  }}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 shrink-0"
                >
                  <UserPlus className="h-4 w-4" />
                  Add member
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-5">
              {listLoading ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                  <Loader2 className="h-8 w-8 animate-spin mb-2" />
                  <p className="text-sm">Loading members…</p>
                </div>
              ) : listMembers.length === 0 ? (
                <p className="text-center text-gray-500 py-12 text-sm">
                  No members match this search for this department. Try clearing search or add a member.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Name</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Phone</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Member ID</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Role</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-600">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {listMembers.map((m) => (
                        <tr key={m.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-900">
                            {m.first_name} {m.last_name}
                          </td>
                          <td className="px-4 py-2 text-gray-700">{m.phone || '—'}</td>
                          <td className="px-4 py-2 text-gray-600 font-mono text-xs">{m.member_id || '—'}</td>
                          <td className="px-4 py-2 text-gray-700 capitalize">{m.role?.replace(/_/g, ' ')}</td>
                          <td className="px-4 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => {
                                navigate(
                                  `/admin/members?department_id=${encodeURIComponent(listModalDept.id)}&edit=${encodeURIComponent(m.id)}`
                                );
                                closeMembersModal();
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-indigo-700 hover:bg-indigo-50 text-sm font-medium"
                            >
                              <Edit className="h-3.5 w-3.5" />
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Department Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {modalMode === 'create' ? 'Create New Department' : 'Edit Department'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* General Error */}
                {errors.general && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                      <p className="text-sm text-red-800">{errors.general}</p>
                    </div>
                  </div>
                )}

                {/* Basic Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Water Department"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Department code will be generated automatically from the name
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Department description and responsibilities"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {modalMode === 'create' ? 'Create Department' : 'Update Department'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentsPage;